import { useEffect, useRef, useCallback, useState } from 'react';

interface UseAutoSaveOptions {
  /**
   * Function to call when auto-saving
   */
  saveFn: () => Promise<void> | void;
  /**
   * Debounce delay in milliseconds (default: 3000ms)
   */
  delay?: number;
  /**
   * Whether auto-save is enabled
   */
  enabled?: boolean;
  /**
   * Guard function that returns true if auto-save should be skipped
   */
  shouldSkip?: () => boolean;
  /**
   * Callback when auto-save starts
   */
  onSaveStart?: () => void;
  /**
   * Callback when auto-save succeeds
   */
  onSaveSuccess?: () => void;
  /**
   * Callback when auto-save fails
   */
  onSaveError?: (error: unknown) => void;
}

interface UseAutoSaveReturn {
  /**
   * Whether auto-save is currently in progress
   */
  isAutoSaving: boolean;
  /**
   * Timestamp of last successful auto-save
   */
  lastAutoSaved: number | null;
  /**
   * Manually trigger auto-save (bypasses debounce)
   */
  triggerSave: () => void;
}

/**
 * Custom hook for auto-saving form data with debounce
 * Uses a cleaner React pattern with useEffect for timer management
 * This replaces direct setTimeout usage with a more React-idiomatic approach
 * 
 * @param dependencies - Array of values to watch for changes
 * @param options - Configuration options
 * @returns Auto-save state and controls
 */
export function useAutoSave<T extends unknown[]>(
  dependencies: T,
  options: UseAutoSaveOptions
): UseAutoSaveReturn {
  const {
    saveFn,
    delay = 3000,
    enabled = true,
    shouldSkip,
    onSaveStart,
    onSaveSuccess,
    onSaveError,
  } = options;

  const previousValuesRef = useRef<string | null>(null);
  const isFirstRenderRef = useRef(true);
  const isAutoSavingRef = useRef(false);
  const saveFnRef = useRef(saveFn);
  const shouldSkipRef = useRef(shouldSkip);
  const onSaveStartRef = useRef(onSaveStart);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const onSaveErrorRef = useRef(onSaveError);
  const enabledRef = useRef(enabled);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState<number | null>(null);
  const dependenciesRef = useRef(dependencies);
  const serializedDependenciesRef = useRef<string>('');

  // Update refs on every render, but only trigger effect when serialized value actually changes
  useEffect(() => {
    dependenciesRef.current = dependencies;
    
    // Serialize and check if it changed
    try {
      const currentSerialized = JSON.stringify(dependencies);
      if (currentSerialized !== serializedDependenciesRef.current) {
        serializedDependenciesRef.current = currentSerialized;
      }
    } catch (_error) {
      // Silently handle serialization errors
    }
  });

  // Keep refs in sync with latest values
  useEffect(() => {
    saveFnRef.current = saveFn;
    shouldSkipRef.current = shouldSkip;
    onSaveStartRef.current = onSaveStart;
    onSaveSuccessRef.current = onSaveSuccess;
    onSaveErrorRef.current = onSaveError;
    enabledRef.current = enabled;
  }, [saveFn, shouldSkip, onSaveStart, onSaveSuccess, onSaveError, enabled]);

  // Perform the actual save
  const performSave = useCallback(async () => {
    // Check guards using refs to get latest values
    if (!enabledRef.current) {
      return;
    }
    if (shouldSkipRef.current && shouldSkipRef.current()) {
      return;
    }
    if (isAutoSavingRef.current) {
      return; // Already saving
    }

    isAutoSavingRef.current = true;
    setIsAutoSaving(true);

    try {
      onSaveStartRef.current?.();
      await saveFnRef.current();
      
      const savedTime = Date.now();
      setLastAutoSaved(savedTime);
      onSaveSuccessRef.current?.();
    } catch (error) {
      onSaveErrorRef.current?.(error);
    } finally {
      isAutoSavingRef.current = false;
      setIsAutoSaving(false);
    }
  }, []); // Empty deps - uses refs for latest values

  // Watch for changes and trigger debounced save
  // Check on every render if serialized dependencies changed
  useEffect(() => {
    // Early return if autosave is disabled
    if (!enabledRef.current) {
      return;
    }

    // Serialize current dependencies
    let currentSerialized: string;
    try {
      currentSerialized = JSON.stringify(dependenciesRef.current);
    } catch (_error) {
      return;
    }

    // Skip first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      // Store initial values
      previousValuesRef.current = currentSerialized;
      serializedDependenciesRef.current = currentSerialized;
      return;
    }
    
    // Check if data has actually changed
    if (previousValuesRef.current === null || currentSerialized === previousValuesRef.current) {
      // No change detected - exit early without setting up timer
      return;
    }

    // Clear any existing timer first (new change detected, so cancel previous timer)
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Update previous values immediately to prevent duplicate triggers
    previousValuesRef.current = currentSerialized;
    serializedDependenciesRef.current = currentSerialized;

    // Set up debounced save using ref to persist across re-renders
    timerRef.current = setTimeout(() => {
      // Clear the ref since timer completed
      timerRef.current = null;
      
      // Re-check guards before saving (they might have changed during the delay)
      if (!enabledRef.current) {
        return;
      }
      if (shouldSkipRef.current && shouldSkipRef.current()) {
        return;
      }
      if (isAutoSavingRef.current) {
        return;
      }
      
      performSave();
    }, delay);

    // No cleanup function - let the timer persist across re-renders
    // The timer will only be cleared if:
    // 1. A new change is detected (we clear it above before setting new timer)
    // 2. The timer completes (timerRef is cleared in the timeout callback)
    // 3. Component unmounts (separate cleanup effect handles this)
  }); // Run on every render - we check for changes inside the effect

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Manual trigger (bypasses debounce)
  const triggerSave = useCallback(() => {
    performSave();
  }, [performSave]);

  return {
    isAutoSaving,
    lastAutoSaved,
    triggerSave,
  };
}

